import React, { useCallback } from 'react';
import { StyleSheet, View } from 'react-native';
import { BodyText, FadeView, Switch, TextField } from '@sweetgreen/garnish';

import { useLocalizationContext } from '@order/Localization';

import { CREDIT_CARDS_ICONS } from '../../../constants/credit-cards';
import type {
  ControlledFormProps,
  CreditCardField,
} from '../CreditCardForm.types';
import { BlockedFields } from './CreditCardForm.blocked';
import { Footer, Header, LineItem } from './CreditCardForm.components';
import { LoadingFields } from './CreditCardForm.loading';

// Section of the credit card form controlled by us instead of Braintree.
export const CreditCardFormControlledContent = ({
  children,
  account,
  fadeDuration,
  subtitle,
  isSaving,
  isDeleting,
  mode,
  footerFullWidth,
  title,
  isLoadingFields,
  canSubmitForm,
  form,
  onFormChange,
  onSubmitForm,
  onDelete,
  excludedFields = [],
}: ControlledFormProps) => {
  const { t } = useLocalizationContext();

  // ─────────────────────── Callbacks ──────────────────────────────────
  const updateFormField = useCallback(
    (field: CreditCardField) => (value: string | number | boolean) => {
      onFormChange({ ...form, [field]: value });
    },
    [form, onFormChange],
  );

  // ─────────────────────── Render ──────────────────────────────────
  return (
    <View style={styles.spacedColumn}>
      <View style={styles.column}>
        <Header subtitle={subtitle} mode={mode} title={title} />

        {/* Loading placeholders for each field */}
        <FadeView duration={fadeDuration} show={isLoadingFields}>
          {mode !== 'edit' && <LoadingFields />}
        </FadeView>

        {/* Section Controlled by Braintree */}
        {children}

        {/* Used when editing credit card, fields disabled */}
        <BlockedFields
          show={Boolean(mode === 'edit')}
          account={account}
          cardType={CREDIT_CARDS_ICONS[account?.cardType ?? 'NEW']}
        />

        {/* Nickname field, not controlled by Braintree */}
        {!excludedFields.includes('nickname') && (
          <LineItem>
            <TextField
              testID="account.credit-card-form.nickname"
              label={t('account.credit-card-form.nickname')}
              value={form.nickname}
              onChangeText={updateFormField('nickname')}
            />
          </LineItem>
        )}

        {/* Default credit card switch, not controlled by Braintree */}
        {!excludedFields.includes('default') && (
          <LineItem style={styles.marginBottom}>
            <View style={styles.row}>
              <View style={styles.column}>
                <BodyText size={3}>
                  {t('account.credit-card-form.default')}
                </BodyText>
              </View>
              <Switch
                testID="account.credit-card-form.default"
                value={form.default}
                onChange={updateFormField('default')}
              />
            </View>
          </LineItem>
        )}
      </View>

      {/* Save / Remove footer, not controlled by Braintree */}
      <Footer
        mode={mode}
        isSaving={isSaving}
        isDeleting={isDeleting}
        canSubmit={canSubmitForm}
        onDelete={onDelete}
        footerFullWidth={footerFullWidth}
        onSubmit={onSubmitForm}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
  },
  column: {
    flex: 1,
  },
  spacedColumn: {
    flex: 1,
    flexGrow: 1,
    justifyContent: 'space-between',
  },
  marginBottom: {
    marginBottom: 0,
  },
});
